import { OrString } from '@motional-cc/fe/interface/OrString';
import { Env } from '@motional-cc/fe/tools/auth';
import { VITE_ENVIRONMENT } from 'src/config/env';

const getHostname = () => {
  // Document is not available in the service worker, we need to use self.
  if (typeof document !== 'undefined') {
    return document.location.hostname;
  }

  if (typeof globalThis.self !== 'undefined') {
    return self.location.hostname;
  }

  throw new Error('Cannot find hostname');
};

type LocalHostAddress =
  | 'localhost'
  | `10.${number}.${number}.${number}`
  | `192.168.${number}.${number}`
  | `172.${number}.${number}.${number}`;

const isALocalHost = (hostname: string): hostname is LocalHostAddress => {
  if (
    hostname === 'localhost' ||
    hostname.startsWith('10.') ||
    hostname.startsWith('192.168.')
  ) {
    return true;
  }

  // Check for 172.16.0.0 - 172.31.255.255 range
  const parts = hostname.split('.');
  if (parts[0] === '172') {
    const secondOctet = parseInt(parts[1], 10);
    return secondOctet >= 16 && secondOctet <= 31;
  }

  return false;
};

// used in tests
// ts-unused-exports:disable-next-line
export const pickEnv = ({
  hostname,
  requestedEnvironment,
}: {
  hostname: string;
  requestedEnvironment?: string;
}) => {
  if (isALocalHost(hostname)) {
    return requestedEnvironment;
  }

  const hostnameParts = hostname.split('.');
  if (hostnameParts.length > 2) {
    const [env, domain] = hostnameParts.slice(-3, -1);
    if (domain === 'motional') {
      return env;
    }
  } else if (hostnameParts.length === 2 && hostnameParts[0] === 'motional') {
    return 'prd';
  }

  throw new Error(
    'Attempting to access the Motional command center through an invalid URL',
  );
};

// <env | 'prd'>.motional.cc
export const env = pickEnv({
  hostname: getHostname(),
  requestedEnvironment: VITE_ENVIRONMENT ?? 'PLEASE_SET',
}) as OrString<Env>;

export const apiServer = `https://api.${env}.motional.cc` as const;
export const armada = `https://armada.${env}.motional.cc` as const;
export const cerebro = `https://cerebro.${env}.motional.cc` as const;
export const concierge = `https://concierge.${env}.motional.cc` as const;
export const deploymentServer = `https://registrar.${env}.motional.cc` as const;
export const fogg = `https://fogg-manager.${env}.motional.cc` as const;
export const geppetto = `https://geppetto.${env}.motional.cc` as const;
export const kamaji = `https://kamaji.${env}.motional.cc` as const;
export const scheduler = `https://scheduler.${env}.motional.cc` as const;
export const notifications = `https://wuphf.${env}.motional.cc` as const;
export const nursery = `https://nursery.${env}.motional.cc` as const;
export const registrar = `https://registrar.${env}.motional.cc` as const;
export const rvaTracker = `https://rva-tracker.${env}.motional.cc` as const;
export const surveillance = `https://surveillance.${env}.motional.cc` as const;
export const user = `https://user-profile.${env}.motional.cc` as const;
export const vehicleStatus =
  `https://vehicle-status.${env}.motional.cc` as const;
export const releaseOrchestration =
  `https://release-orchestration.${env}.motional.cc` as const;
export const webPush = `https://web-push.${env}.motional.cc` as const;

// only for back-end
export const dataColosseum =
  `https://data-colosseum.${env}.motional.cc` as const;
export const elyvator = `https://elyvator.${env}.motional.cc` as const;
export const gringotts = `https://gringotts.${env}.motional.cc` as const;
export const jiraListener = `https://jira-listener.${env}.motional.cc` as const;
export const nectar = `https://nectar.${env}.motional.cc` as const;
export const palantiri = `https://palantiri.${env}.motional.cc` as const;

// No longer in use
// export const dataColosseum = `https://data-colosseum.${env}.motional.cc` as const;
// export const map = `https://editor.maps.motional.com/api/v2`;
// export const simcar = `wss://muletto.${env}.motional.cc`;
